import React from "react";
import PageHero from "../components/PageHero";
import { Target, Eye } from "lucide-react";

export default function WhyDuramix() {
  return (
    <main className="bg-white">

      {/* HERO */}
      <PageHero
        title="About SSC READYMIX"
        subtitle="Engineered ready mix concrete solutions built on precision, quality, reliability, and long term performance."
        num="01"
      />

      {/* ABOUT SECTION */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">

          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* LEFT */}
            <div>

              <div className="inline-flex items-center gap-2 rounded-full border border-[#ececef] bg-[#fafafa] px-5 py-2 mb-8">
                <span className="w-2 h-2 rounded-full bg-[#ef2435]" />

                <span className="text-[11px] tracking-[0.3em] uppercase font-semibold text-[#ef2435]">
                  About SSC Readymix
                </span>
              </div>

              <h2 className="text-4xl md:text-6xl font-black text-gray-900 leading-tight mb-8">
                WE CONCRETE
                <br />
                <span className="text-[#ef2435]">
                  YOUR DREAM
                </span>
              </h2>

              <div className="w-24 h-[4px] bg-[#ef2435] rounded-full mb-10" />

              <div className="space-y-7 text-gray-600 leading-[2] text-[17px]">

                <p>
                  SSC READYMIX stands as a trusted provider of engineered ready mix concrete solutions, supporting the growing demands of modern construction.
                </p>

                <p>
                  Our operations are built around precision driven processes, consistent quality standards, and disciplined execution, ensuring reliable performance across every project we serve.
                </p>

                <p>
                  With advanced batching systems, stringent quality control practices, and an efficiently managed logistics network, we deliver concrete solutions tailored to specific structural and environmental requirements.
                </p>

                <p>
                  Each mix is designed to perform under real site conditions, maintaining strength, workability, and durability from plant to placement.
                </p>

                <p>
                  Serving residential, commercial, and infrastructure developments, we work closely with engineers, contractors, and developers to ensure timely delivery and seamless project execution.
                </p>

                <p>
                  Our focus remains on maintaining clarity in coordination, efficiency in operations, and consistency in results.
                </p>

                <div className="border-l-4 border-[#ef2435] pl-6 py-2 bg-[#fafafa] rounded-r-2xl">
                  <p className="text-2xl font-bold text-gray-900 leading-relaxed">
                    “SSC READYMIX,
                    <br />
                    WE CONCRETE YOUR DREAM”
                  </p>
                </div>

                <p>
                  We continue to build with responsibility, reliability, and a commitment to long term performance.
                </p>

              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-8 lg:sticky lg:top-32">

              {/* MISSION */}
              <div className="rounded-[32px] border border-[#ececef] bg-[#fafafa] p-8 md:p-10">

                <div className="w-14 h-14 rounded-2xl bg-[#ef2435] flex items-center justify-center mb-6">
                  <Target className="text-white" size={24} />
                </div>

                <p className="text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-3">
                  Mission
                </p>

                <h3 className="text-3xl font-black text-gray-900 mb-5">
                  Our Mission
                </h3>

                <p className="text-gray-600 leading-[2] text-[16px]">
                  To deliver high quality ready mix concrete solutions through advanced technology, strict quality standards, efficient operations, and customer focused service.
                </p>
              </div>

              {/* VISION */}
              <div className="rounded-[32px] bg-[#111] text-white p-8 md:p-10 relative overflow-hidden">

                <div className="absolute top-0 right-0 w-60 h-60 bg-[#ef2435]/20 rounded-full blur-3xl" />

                <div className="relative">

                  <div className="w-14 h-14 rounded-2xl bg-[#ef2435] flex items-center justify-center mb-6">
                    <Eye className="text-white" size={24} />
                  </div>

                  <p className="text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-3">
                    Vision
                  </p>

                  <h3 className="text-3xl font-black mb-5">
                    Our Vision
                  </h3>

                  <p className="text-gray-300 leading-[2] text-[16px]">
                    To become one of Odisha’s leading ready mix concrete companies known for quality, trust, reliability, and long term impact.
                  </p>

                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* MANAGING DIRECTOR */}
      <section className="py-24 bg-[#fafafa] border-y border-[#ececef]">

        <div className="max-w-[1400px] mx-auto px-5 md:px-8">

          <div className="grid lg:grid-cols-[420px,1fr] gap-14 items-center">

            {/* IMAGE */}
            <div className="relative">

              <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-[#ef2435]/10 blur-3xl" />

              <div className="relative rounded-[40px] overflow-hidden border border-[#ececef] bg-white shadow-[0_20px_80px_rgba(0,0,0,0.06)]">

                <img
                  src={`https://admin.sscreadymix.com/uploads/Benudhar.jpg?v=${new Date().getTime()}`}
                  alt="Managing Director"
                  className="w-full h-[560px] object-cover"
                />

              </div>
            </div>

            {/* CONTENT */}
            <div>

              <span className="inline-flex items-center gap-2 text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-5">
                <span className="w-10 h-[2px] bg-[#ef2435]" />
                Vision of the Managing Director
              </span>

              <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-8">
                Building Strong
                <span className="text-[#ef2435]"> People.</span>
              </h2>

              <div className="space-y-6 text-gray-600 leading-[2] text-[17px]">

                <p>
                  As the Managing Director of SSC READYMIX, I have always believed that building strong structures begins with building strong people.
                </p>

                <p>
                  For me, this journey is not only about concrete or construction it is about creating opportunities that can transform lives.
                </p>

                <p>
                  My vision is to ensure that this organization becomes a platform where aspirations are supported, nurtured, and fulfilled.
                </p>

                <p>
                  I am deeply committed to generating meaningful employment and empowering the youth of our region.
                </p>

                <p>
                  It is my goal to create opportunities for more than 1000 young individuals, enabling them to build stable careers, gain confidence, and contribute with pride to the nation’s growth.
                </p>

                <p>
                  As we continue to expand, my focus remains clear: to build an organization that stands not only for quality and reliability, but also for trust, responsibility, and lasting impact on people’s lives.
                </p>

              </div>

              <div className="mt-10 pt-8 border-t border-[#ececef]">

                <h3 className="text-2xl font-black text-gray-900">
                  BENUDHAR PRADHAN
                </h3>

                <p className="text-[#ef2435] font-semibold mt-2">
                  Managing Director
                </p>

                <p className="text-gray-500 mt-1">
                  SSC READYMIX Pvt. Ltd.
                </p>

              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CEO */}
      <section className="py-24 bg-white">

        <div className="max-w-[1400px] mx-auto px-5 md:px-8">

          <div className="grid lg:grid-cols-[1fr,420px] gap-14 items-center">

            {/* CONTENT */}
            <div>

              <span className="inline-flex items-center gap-2 text-[#ef2435] text-xs tracking-[0.3em] uppercase font-semibold mb-5">
                <span className="w-10 h-[2px] bg-[#ef2435]" />
                Vision of the CEO
              </span>

              <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-8">
                Building a Legacy
                <span className="text-[#ef2435]"> of Trust.</span>
              </h2>

              <div className="space-y-6 text-gray-600 leading-[2] text-[17px]">

                <p>
                  As the CEO of SSC READYMIX, my vision is to establish our organization as one of the leading ready mix concrete suppliers in Odisha, known for its consistency, reliability, and commitment to excellence.
                </p>

                <p>
                  SSC READYMIX is not just a company. It is a family built on trust, shared values, and collective growth beyond blood relations.
                </p>

                <p>
                  By focusing on quality driven processes, advanced systems, and efficient operations, we aim to deliver concrete solutions that our clients can depend on with complete confidence.
                </p>

                <p>
                  Our goal is not only to expand our presence, but to build a legacy of trust, responsibility, and long term impact.
                </p>

              </div>

              <div className="mt-10 pt-8 border-t border-[#ececef]">

                <h3 className="text-2xl font-black text-gray-900">
                  SHANTI SAGAR SWAIN
                </h3>

                <p className="text-[#ef2435] font-semibold mt-2">
                  CEO
                </p>

                <p className="text-gray-500 mt-1">
                  SSC GROUP
                </p>

              </div>

            </div>

            {/* IMAGE */}
            <div className="relative">

              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-[#ef2435]/10 blur-3xl" />

              <div className="relative rounded-[40px] overflow-hidden border border-[#ececef] bg-white shadow-[0_20px_80px_rgba(0,0,0,0.06)]">

                <img
                  src={`https://admin.sscreadymix.com/uploads/Shanti.jpg?v=${new Date().getTime()}`}
                  alt="CEO"
                  className="w-full h-[560px] object-cover"
                />

              </div>
            </div>

          </div>
        </div>
      </section>

    </main>
  );
}